import {nodesConfig} from '@/config/canvas'
import {SecondaryButton} from '@/shared-components/Button'
import {InputSelector} from '@/shared-components/InputSelector/InputSelector'
import {Spinner} from '@/shared-components/Spinner'
import {useAppSelector} from '@/store'
import {JourneyType, JourneyTypeSchema} from '@/types/JourneyType'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {GetStepTypesRequest} from '@/types/StepTypes'
import {getStepTypesUseCase} from '@/useCases/getStepTypesUseCase'
import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {useIntl} from 'react-intl'

export type AddStepTypeSelectorProps = {
  scope: GetStepTypesRequest['scope']
  onChange: (stepType: NodeTypeEnum) => void
  journeyType?: JourneyType
  stepType?: NodeTypeEnum
  isLastStep?: boolean
}

export const AddStepTypeSelector = ({scope, isLastStep, stepType, onChange, journeyType}: AddStepTypeSelectorProps) => {
  const {formatMessage} = useIntl()
  const country = useAppSelector(state => state.journeyConfig.country)
  const {application} = useAppSelector(state => state.common)
  const {data, isPending, isError, isFetching, refetch} = useQuery({
    queryKey: ['stepTypes', scope, country, application],
    queryFn: ({signal}) => getStepTypesUseCase({scope}, signal),
    staleTime: 3 * 60 * 1000,
    retry: false,
  })

  const options = useMemo(() => {
    const availableStepTypesSet = new Set(data?.stepTypes.map(({code}) => code) ?? [])
    return nodesConfig
      .filter(node => availableStepTypesSet.has(node.type))
      .filter(hideRequiresInterventionStep(isLastStep))
      .filter(hideOneTimeSteps(journeyType))
  }, [isLastStep, journeyType, data])

  return (
    <>
      <InputSelector
        id="step-type"
        label={formatMessage({id: 'JOURNEY_BUILDER.ADD_NEW_STEP_TYPE_SELECT'})}
        value={stepType ?? ''}
        disabled={isPending || isError}
        error={isError ? formatMessage({id: 'SEND_NOTIFICATION_CONTENT.ERROR_LOADING_STEP'}) : undefined}
        onChange={evt => onChange(evt.target.value as NodeTypeEnum)}
        className="shadow-none mb-4 w-full max-w-xs border-border text-sm outline-none hover:border-border-hover"
      >
        <option value="-">{formatMessage({id: 'JOURNEY_BUILDER.ADD_NEW_SELECT_STEP_EMPTY_OPTION'})}</option>
        {options.map(({name: label, type: optionType}) => {
          return (
            <option value={optionType} key={optionType}>
              {label}
            </option>
          )
        })}
      </InputSelector>
      {isPending && <Spinner size="medium" data-testid="loading-step-types" />}
      {isError && (
        <SecondaryButton
          size="small"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="step-types-try-again-button"
        >
          {formatMessage({id: 'GENERAL.TRY_AGAIN_BUTTON'})}
        </SecondaryButton>
      )}
    </>
  )
}

const hideRequiresInterventionStep = (isLastStep = false) => {
  return ({type}: {type: NodeTypeEnum}) => {
    return type !== NodeTypeEnum.requiresIntervention || isLastStep
  }
}

const hideOneTimeSteps = (journeyType?: JourneyType) => {
  return ({type}: {type: NodeTypeEnum}) => {
    if (journeyType === JourneyTypeSchema.enum.ONE_TIME) {
      return ![NodeTypeEnum.wait, NodeTypeEnum.requiresIntervention].includes(type)
    }
    return true
  }
}
